import React, { useEffect, useState, useCallback } from 'react';
import {
    Button,
    TextField,
    Grid,
    InputAdornment,
    FormLabel,
    FormControl,
    FormControlLabel,
    Checkbox,
    FormGroup,
    Typography,
    Box,
    Chip
} from '@material-ui/core';
import { Dialog } from 'components';
import { create, update, getUnit, getEvents } from './services/http';
import NumberFormat from 'react-number-format';
import EuroSymbolIcon from '@material-ui/icons/EuroSymbol';
import EventIcon from '@material-ui/icons/Event';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(({ palette, shape, spacing }) => ({
    eventsContainer: {
        backgroundColor: palette.action.hover,
        borderRadius: shape.borderRadius,
        border: `1px solid ${palette.divider}`,
        padding: spacing(1.5)
    },
    eventsHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing(1),
        marginBottom: spacing(1)
    },
    eventItem: {
        borderRadius: shape.borderRadius,
        border: '1px solid transparent',
        marginLeft: 0,
        padding: '2px 8px',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': { backgroundColor: palette.action.hover }
    },
    eventItemChecked: {
        borderColor: palette.primary.light,
        backgroundColor: `${palette.primary.light}22`
    },
    selectionChip: { height: 20, fontSize: 11 }
}));

// ─── Constantes ────────────────────────────────────────────────────
const EMPTY_STATE = { label: '', amount: '', selectedEvents: [] };

// ─── Sous-composant : champ prix ───────────────────────────────────
function PriceTextField({ inputRef, allowedToChangePrice, noIcon, ...props }) {
    return (
        <TextField
            {...props}
            variant="outlined"
            fullWidth
            required
            helperText="Saisir un prix unitaire"
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <EuroSymbolIcon />
                    </InputAdornment>
                )
            }}
        />
    );
}

// ─── Composant principal ───────────────────────────────────────────
const FormDialog = ({ id, open, close }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [{ label, amount, selectedEvents }, setForm] = useState(EMPTY_STATE);

    // Helpers d'état ─────────────────────────────────────────────────
    const setField = useCallback(
        field => value => setForm(prev => ({ ...prev, [field]: value })),
        []
    );

    const toggleEvent = useCallback(eventId => {
        setForm(prev => ({
            ...prev,
            selectedEvents: prev.selectedEvents.includes(eventId)
                ? prev.selectedEvents.filter(i => i !== eventId)
                : [...prev.selectedEvents, eventId]
        }));
    }, []);

    const reset = useCallback(() => {
        close();
        setForm(EMPTY_STATE);
    }, [close]);

    // Chargement ─────────────────────────────────────────────────────
    useEffect(() => {
        getEvents()
            .then(setEvents)
            .catch(console.error);
    }, []);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getUnit(id)
            .then(({ libelle, amount, events }) =>
                setForm({ label: libelle, amount, selectedEvents: events?.map(e => e.id) ?? [] })
            )
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id]);

    // Soumission ─────────────────────────────────────────────────────
    const submit = useCallback(() => {
        const payload = { libelle: label, amount, events: selectedEvents };
        setLoading(true);
        (id ? update(id, payload) : create(payload))
            .then(reset)
            .catch(err => alert(err))
            .finally(() => setLoading(false));
    }, [id, label, amount, selectedEvents, reset]);

    // Rendu ──────────────────────────────────────────────────────────
    return (
        <Dialog
            disabled={loading}
            title={id ? 'Modifier une unité' : 'Enregistrer une unité'}
            onClose={reset}
            open={open}
            action={
                <Button disabled={loading} onClick={submit} variant="contained" color="primary">
                    {id ? 'Modifier' : 'Enregistrer'}
                </Button>
            }>
            <Grid container direction="column" spacing={2}>
                <Grid item>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Unité"
                        helperText="Saisir un libellé pour l'unité"
                        required
                        value={label}
                        onChange={e => setField('label')(e.target.value)}
                        disabled={loading}
                    />
                </Grid>

                <Grid item>
                    <NumberFormat
                        label="Prix unitaire"
                        value={amount}
                        customInput={PriceTextField}
                        onValueChange={({ value }) => setField('amount')(value)}
                        thousandsGroupStyle="fr"
                        thousandSeparator=" "
                        decimalSeparator=","
                        type="text"
                        allowedDecimalSeparators={[',', '.', '?', ';']}
                        allowNegative={false}
                        decimalScale={2}
                        isNumericString
                        disabled={loading}
                    />
                </Grid>

                <Grid item>
                    <FormControl component="fieldset" fullWidth>
                        <Box className={classes.eventsContainer}>
                            <Box className={classes.eventsHeader}>
                                <EventIcon fontSize="small" color="action" />
                                <FormLabel component="legend">
                                    <Typography variant="body2" component="span">
                                        Évènements
                                    </Typography>
                                </FormLabel>
                                {selectedEvents.length > 0 && (
                                    <Chip
                                        className={classes.selectionChip}
                                        color="primary"
                                        size="small"
                                        label={`${selectedEvents.length} sélectionné${
                                            selectedEvents.length > 1 ? 's' : ''
                                        }`}
                                    />
                                )}
                            </Box>

                            <FormGroup>
                                {events.map(({ id, libelle }) => (
                                    <FormControlLabel
                                        key={id}
                                        className={`${classes.eventItem} ${
                                            selectedEvents.includes(id)
                                                ? classes.eventItemChecked
                                                : ''
                                        }`}
                                        label={libelle}
                                        control={
                                            <Checkbox
                                                checked={selectedEvents.includes(id)}
                                                onChange={() => toggleEvent(id)}
                                                color="primary"
                                                disabled={loading}
                                            />
                                        }
                                    />
                                ))}
                            </FormGroup>
                        </Box>
                    </FormControl>
                </Grid>
            </Grid>
        </Dialog>
    );
};

export default FormDialog;
